import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { brDate } from "../../format";
import { fmtBRL } from "../../utils/formatters";
import { getExposicao } from "../../api/client";
import type { ExposicaoCliente, ExposicaoLinha, ExposicaoResponse } from "../../api/types";
import { MetricChip } from "./MetricChip";
import { AsOfBadge } from "../ui/AsOfBadge";

// #30 IPCA ON: seletores cientes do modo. Indexado lê os campos *_indexado
// (fallback ao nominal em payload pré-#30, onde ainda não existem). Reajusta SÓ
// a perna de contrato — posição (MWm) é a mesma nos dois modos (IPCA não mexe vol).
const mtmLinha = (l: ExposicaoLinha, idx: boolean): number =>
  idx ? l.mtm_liquido_indexado ?? l.mtm_liquido : l.mtm_liquido;
const mtmCliente = (c: ExposicaoCliente, idx: boolean): number =>
  idx ? c.mtm_liquido_indexado ?? c.mtm_liquido : c.mtm_liquido;

/** Posição em MWm: sinal + 1 casa; |x|<0,05 → "≈ 0,0" (casado). */
function fmtMWm(v: number): string {
  if (Math.abs(v) < 0.05) return "≈ 0,0";
  return (v > 0 ? "+" : "") + v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function corValor(v: number): string {
  if (Math.abs(v) < 0.05) return "text-white/40";
  return v >= 0 ? "text-indra-positivo" : "text-indra-negativo";
}

const BADGE_CLS: Record<string, string> = {
  buy: "bg-indra-positivo/12 text-indra-positivo",
  sell: "bg-indra-negativo/12 text-indra-negativo",
  both: "bg-amber-400/12 text-amber-400",
};

function DirBadge({ tipo, label }: { tipo: string; label: string }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded ml-2 font-semibold align-middle ${BADGE_CLS[tipo] ?? "bg-white/10 text-white/60"}`}>
      {label}
    </span>
  );
}

function Metric({ k, v, tone, sm }: { k: string; v: string; tone?: "pos" | "neg"; sm?: boolean }) {
  const c = tone === "pos" ? "text-indra-positivo" : tone === "neg" ? "text-indra-negativo" : "text-white";
  return (
    <div className="flex flex-col">
      <span className="text-white/40 text-[10px] uppercase tracking-wide">{k}</span>
      <span className={`font-mono font-bold mt-0.5 ${sm ? "text-sm" : "text-[17px]"} ${c}`}>{v}</span>
    </div>
  );
}

/** Aba "Por cliente" do drill guarda-chuva: concentração + barra top-10 vs cauda +
 *  top-15 + cauda colapsada + busca. Nunca despeja a cauda inteira. */
function PorCliente({ l, indexado }: { l: ExposicaoLinha; indexado: boolean }) {
  const [q, setQ] = useState("");
  // Sob Indexado usa a concentração recomputada no MtM reajustado (ranking pode
  // diferir); fallback à nominal em payload pré-#30.
  const conc = (indexado ? l.detalhe.concentracao_indexado : null) ?? l.detalhe.concentracao!;
  // Re-ordena os clientes pelo MtM do modo ativo (a tabela default vem nominal).
  const clientes = useMemo(
    () => [...l.detalhe.por_cliente].sort((a, b) => Math.abs(mtmCliente(b, indexado)) - Math.abs(mtmCliente(a, indexado))),
    [l.detalhe.por_cliente, indexado],
  );
  const busca = q.trim().toLowerCase();
  const filtrados = busca ? clientes.filter((c) => c.nome.toLowerCase().includes(busca)) : clientes;
  const TOP = 15;
  const mostra = busca ? filtrados : filtrados.slice(0, TOP);
  const cauda = busca ? [] : filtrados.slice(TOP);
  const caudaMtm = cauda.reduce((s, c) => s + mtmCliente(c, indexado), 0);
  const top10pct = Math.round(conc.top10_share * 100);
  return (
    <div>
      <div className="bg-indra-azul-deep border border-white/10 rounded-lg p-4 mb-4">
        <div className="flex gap-7 flex-wrap items-baseline">
          <Metric k="Clientes" v={String(conc.n_clientes)} />
          <Metric k="Top-10 concentram" v={`${top10pct}%`} tone="neg" />
          <Metric
            k="Maior cliente"
            v={`${fmtBRL(conc.maior_cliente.mtm_liquido, { abbreviate: true })} (${Math.round(conc.maior_cliente.share * 100)}%)`}
            tone={conc.maior_cliente.mtm_liquido >= 0 ? "pos" : "neg"} sm
          />
          <Metric k="Exposição varejo" v={fmtBRL(mtmLinha(l, indexado), { abbreviate: true })} tone={mtmLinha(l, indexado) >= 0 ? "pos" : "neg"} sm />
        </div>
        <div className="h-[9px] rounded-md overflow-hidden flex mt-3 bg-indra-azul-surface">
          <div className="h-full bg-indra-negativo/80" style={{ width: `${top10pct}%` }} />
          <div className="h-full bg-indra-negativo/30" style={{ width: `${100 - top10pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-white/40 mt-1.5">
          <span>top-10 · {fmtBRL(conc.top10_mtm, { abbreviate: true })}</span>
          <span>cauda ({conc.tail_n} clientes) · {fmtBRL(conc.tail_mtm, { abbreviate: true })}</span>
        </div>
      </div>
      <input
        value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔎 buscar cliente específico…"
        className="w-full bg-indra-azul-deep border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 mb-2 focus:outline-none focus:border-white/30"
      />
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left py-2 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Cliente</th>
            <th className="text-right py-2 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Posição líq. (MWm méd.)</th>
            <th className="text-right py-2 px-2.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">MtM líquido</th>
          </tr>
        </thead>
        <tbody>
          {mostra.map((c, i) => (
            <tr key={c.cnpj ?? c.nome + i} className="border-b border-white/5 hover:bg-white/5">
              <td className="py-2 px-2.5 text-sm text-white">
                <span className="text-white/30 inline-block w-5">{busca ? "" : i + 1}</span>{c.nome}
              </td>
              <td className={"py-2 px-2.5 text-right font-mono text-[13px] " + corValor(c.posicao_liquida_mwm)}>{fmtMWm(c.posicao_liquida_mwm)}</td>
              <td className={"py-2 px-2.5 text-right font-mono text-[13px] whitespace-nowrap " + corValor(mtmCliente(c, indexado))}>{fmtBRL(mtmCliente(c, indexado), { decimals: 0 })}</td>
            </tr>
          ))}
          {mostra.length === 0 && (
            <tr><td colSpan={3} className="py-4 text-center text-white/40 text-sm">Nenhum cliente casa com “{q}”.</td></tr>
          )}
          {cauda.length > 0 && (
            <tr className="text-white/55 italic">
              <td colSpan={2} className="py-3 px-2.5 text-sm">▸ + {cauda.length} outros clientes <span className="text-white/30">· menores</span></td>
              <td className={"py-3 px-2.5 text-right font-mono whitespace-nowrap " + corValor(caudaMtm)}>{fmtBRL(caudaMtm, { decimals: 0 })}</td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-[10px] text-white/35 mt-3 leading-relaxed">
        Risco de crédito CCEE é da Indra (clientes sob a Comercializadora) — a linha-mãe é agregada. Esta visão abre o
        per-cliente pra leitura de concentração. Score/limite/consumo por cliente ficam no engine de crédito (#30).
      </p>
    </div>
  );
}

function Drill({ l, indexado }: { l: ExposicaoLinha; indexado: boolean }) {
  const p = l.detalhe.posicao;
  const m = l.detalhe.mtm;
  // Pernas de MtM cientes do modo (perna de contrato reajustada sob Indexado).
  const mCompra = indexado ? m.compra_indexado ?? m.compra : m.compra;
  const mVenda = indexado ? m.venda_indexado ?? m.venda : m.venda;
  const mLiquido = indexado ? m.liquido_indexado ?? m.liquido : m.liquido;
  const anos = porAno(p.por_ano_submercado);
  const [tab, setTab] = useState<"cliente" | "ano">(l.is_umbrella ? "cliente" : "ano");
  const tabCls = (on: boolean) =>
    "text-xs px-3 py-1.5 rounded-md cursor-pointer border " +
    (on ? "bg-indra-azul-deep text-white border-white/10 font-semibold" : "text-white/55 border-transparent hover:text-white");
  const painelAno = (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <h4 className="text-[13px] font-bold text-white mb-2">
            Posição <span className="text-white/40 font-normal text-xs">· "qual minha posição com {l.contraparte}?"</span>
          </h4>
          <Leg label="Compra (compramos dela)" valor={`${fmtMWm(p.compra_mwm)} MWm`} cor={corValor(p.compra_mwm)} />
          <Leg label="Venda (vendemos a ela)" valor={`${fmtMWm(p.venda_mwm)} MWm`} cor={corValor(p.venda_mwm)} />
          <div className="flex justify-between font-mono font-extrabold text-sm border-t border-white/15 mt-1 pt-2">
            <span className="font-sans text-white">
              Posição líquida{" "}
              <span className="text-white/40 text-[11px] font-medium">{periodoTag(anos)}</span>
            </span>
            <span className={corValor(p.liquida_mwm)}>{fmtMWm(p.liquida_mwm)} MWm · {l.direcao}</span>
          </div>
          <TemporalBar anos={anos} />
        </div>
        <div>
          <h4 className="text-[13px] font-bold text-white mb-2">
            MtM <span className="text-white/40 font-normal text-xs">· "qual meu MtM com {l.contraparte}?"</span>
          </h4>
          <Leg label="MtM da compra" valor={fmtBRL(mCompra, { decimals: 0 })} cor={corValor(mCompra)} />
          <Leg label="MtM da venda" valor={fmtBRL(mVenda, { decimals: 0 })} cor={corValor(mVenda)} />
          <Leg label="MtM líquido" valor={fmtBRL(mLiquido, { decimals: 0 })} cor={corValor(mLiquido)} net />
          <div className="mt-3">
            {m.por_ano.map((r, i) => {
              const v = indexado ? r.mtm_indexado ?? r.mtm : r.mtm;
              return (
                <div key={i} className="flex justify-between text-xs text-white/55 font-mono py-0.5">
                  <span>{r.ano}{i === 0 ? " · marcado vs PLD/forward" : ""}</span>
                  <b className={corValor(v) + " font-semibold"}>{fmtBRL(v, { abbreviate: true })}</b>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-white/35 mt-4 leading-relaxed">
        Posição líq. = MWm MÉDIO no span (potência média = Σ MWh / Σ horas de entrega de todos os anos; NÃO soma os anos — potência não se soma). Barras = MWm por ano de fornecimento (mediam o header). MtM marcado contra PLD realizado (meses fechados) e curva forward (futuro), spread 0 · MtM SOMA os anos (R$ aditivo) · reconciliável com o MtM global da Mesa.
      </p>
    </>
  );
  return (
    <div className="px-5 py-5 bg-indra-azul-deep/60 border-t border-white/5">
      {l.is_umbrella && (
        <div className="flex gap-1 mb-4">
          <button type="button" onClick={() => setTab("cliente")} className={tabCls(tab === "cliente")}>Por cliente</button>
          <button type="button" onClick={() => setTab("ano")} className={tabCls(tab === "ano")}>Por ano × submercado</button>
        </div>
      )}
      {l.is_umbrella && tab === "cliente" ? <PorCliente l={l} indexado={indexado} /> : painelAno}
    </div>
  );
}

function Leg({ label, valor, cor, net }: { label: string; valor: string; cor: string; net?: boolean }) {
  return (
    <div
      className={
        "flex justify-between font-mono text-[13px] py-1.5 " +
        (net ? "border-t border-white/15 mt-1 pt-2 font-extrabold text-sm" : "border-b border-dashed border-white/10")
      }
    >
      <span className={net ? "text-white" : "text-white/55 font-sans"}>{label}</span>
      <span className={cor}>{valor}</span>
    </div>
  );
}

// --- Período na posição (#30 fast-follow): agrega por_ano_submercado por ANO ---

type AnoNet = { ano: number; net: number; subs: { submercado: string; mwm: number }[] };
const EPS_MWM = 0.05;

function porAno(pas: { ano: number; submercado: string; mwm: number }[]): AnoNet[] {
  const m = new Map<number, AnoNet>();
  for (const r of pas) {
    const e = m.get(r.ano) ?? { ano: r.ano, net: 0, subs: [] };
    e.net += r.mwm;
    e.subs.push({ submercado: r.submercado, mwm: r.mwm });
    m.set(r.ano, e);
  }
  return [...m.values()].sort((a, b) => a.ano - b.ano);
}

function rangeAnos(anos: number[]): string {
  if (anos.length === 0) return "";
  if (anos.length === 1) return String(anos[0]);
  return `${anos[0]}-${String(anos[anos.length - 1]).slice(2)}`;
}

/** "· 2026" (1 ano) | "· short 2026-27, long 2028-29" (misto) — pro inline da líquida. */
function periodoTag(anos: AnoNet[]): string {
  if (anos.length === 0) return "";
  if (anos.length === 1) return `· ${anos[0].ano}`;
  const shorts = anos.filter((a) => a.net < -EPS_MWM).map((a) => a.ano);
  const longs = anos.filter((a) => a.net > EPS_MWM).map((a) => a.ano);
  const parts: string[] = [];
  if (shorts.length) parts.push(`short ${rangeAnos(shorts)}`);
  if (longs.length) parts.push(`long ${rangeAnos(longs)}`);
  return parts.length ? `· ${parts.join(", ")}` : `· ${rangeAnos(anos.map((a) => a.ano))}`;
}

/** Barra temporal com sinal: 1 bloco/ano, ↑ comprado (verde) / ↓ vendido (vermelho),
 *  altura ∝ |MWm net do ano|; submercado no hover. */
function TemporalBar({ anos }: { anos: AnoNet[] }) {
  const maxAbs = Math.max(...anos.map((a) => Math.abs(a.net)), 0.01);
  // Caixa 120px, zero centrado em 52. Meia-altura útil H=34 deixa folga pros
  // labels: comprado → valor ACIMA do topo da barra (top 39−h ≥ 5); vendido →
  // valor ABAIXO (top 54+h ≤ 88) e ano fixo em 104, sem sobreposição.
  const H = 34;
  return (
    <div className="mt-4">
      <div className="text-[11px] text-white/55 mb-2">
        posição líquida por ano de fornecimento (↑ comprado · ↓ vendido)
      </div>
      <div className="flex gap-2.5 items-stretch">
        {anos.map((a) => {
          const h = Math.max(2, (Math.abs(a.net) / maxAbs) * H);
          const buy = a.net >= 0;
          const hover = a.subs.map((s) => `${s.submercado}: ${fmtMWm(s.mwm)} MWm`).join(" · ");
          return (
            <div key={a.ano} title={hover} className="flex-1 relative h-[120px] border-l border-white/5 first:border-l-0">
              <div className="absolute left-0 right-0 top-[52px] border-t border-dashed border-white/20" />
              <div
                className={"absolute left-[18%] right-[18%] rounded-[3px] " + (buy ? "bg-indra-positivo/80" : "bg-indra-negativo/80")}
                style={buy ? { bottom: 68, height: h } : { top: 52, height: h }}
              />
              <div
                className={"absolute left-0 right-0 text-center font-mono text-[10px] font-bold " + (buy ? "text-indra-positivo" : "text-indra-negativo")}
                style={{ top: buy ? 39 - h : 54 + h }}
              >
                {fmtMWm(a.net)}
              </div>
              <div className="absolute left-0 right-0 text-center font-mono text-[10px] text-white/40" style={{ top: 104 }}>{a.ano}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Indicador compacto de período na célula: 1 ano → rótulo; vários → sparkbar+range. */
function SparkPeriodo({ anos }: { anos: AnoNet[] }) {
  if (anos.length <= 1) return <span className="text-[10px] text-white/40">{anos[0]?.ano ?? ""}</span>;
  const maxAbs = Math.max(...anos.map((a) => Math.abs(a.net)), 0.01);
  const range = `${String(anos[0].ano).slice(2)}→${String(anos[anos.length - 1].ano).slice(2)}`;
  return (
    <span className="flex gap-[2px] items-center h-3.5 justify-end">
      {anos.map((a) => (
        <i
          key={a.ano}
          title={`${a.ano}: ${fmtMWm(a.net)} MWm`}
          className={"w-[5px] rounded-[1px] block " + (a.net >= 0 ? "bg-indra-positivo" : "bg-indra-negativo")}
          style={{ height: Math.max(3, (Math.abs(a.net) / maxAbs) * 13) }}
        />
      ))}
      <span className="text-[10px] text-white/40 ml-0.5">{range}</span>
    </span>
  );
}

export function ExposicaoTab() {
  const [data, setData] = useState<ExposicaoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  // #30 IPCA ON: toggle Nominal·Indexado (default Nominal, igual o CF por Book
  // #64). Troca headlines + tabela + drill SEM refetch (o payload traz os dois).
  const [indexado, setIndexado] = useState(false);
  // null = ordem default (|MtM| desc); senão ordena pela coluna clicada.
  const [sortCol, setSortCol] = useState<"mtm" | "mwm" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let vivo = true;
    getExposicao()
      .then((r) => vivo && setData(r))
      .catch((e) => vivo && setErro(String(e)));
    return () => {
      vivo = false;
    };
  }, []);

  // Filtro (nome, client-side, case-insensitive) + ordenação — sobre o payload
  // que já veio. Os headlines NÃO mudam (seguem o total da Mesa).
  const linhasView = useMemo(() => {
    if (!data) return [];
    const q = busca.trim().toLowerCase();
    const arr = q
      ? data.linhas.filter((l) => l.contraparte.toLowerCase().includes(q))
      : [...data.linhas];
    const sgn = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortCol === "mtm") return sgn * (mtmLinha(a, indexado) - mtmLinha(b, indexado));
      if (sortCol === "mwm") return sgn * (a.posicao_liquida_mwm - b.posicao_liquida_mwm);
      return Math.abs(mtmLinha(b, indexado)) - Math.abs(mtmLinha(a, indexado)); // default |MtM| desc
    });
    return arr;
  }, [data, busca, sortCol, sortDir, indexado]);

  function clickSort(col: "mtm" | "mwm") {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("desc");
    }
  }
  const seta = (col: "mtm" | "mwm") => (sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  if (erro) return <div className="text-indra-negativo text-sm">Falha ao carregar Exposição: {erro}</div>;
  if (!data) return <div className="text-white/50 text-sm">Carregando exposição…</div>;

  // Headlines cientes do modo (fallback ao nominal em payload pré-#30).
  const hl = indexado
    ? {
        total: data.mtm_liquido_total_indexado ?? data.mtm_liquido_total,
        compradas: data.total_net_compradas_indexado ?? data.total_net_compradas,
        vendidas: data.total_net_vendidas_indexado ?? data.total_net_vendidas,
      }
    : {
        total: data.mtm_liquido_total,
        compradas: data.total_net_compradas,
        vendidas: data.total_net_vendidas,
      };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white font-sans">Relatório Diário — Controle de Risco (MtM)</h2>
          <p className="text-white/50 text-sm mt-1">
            Referência · {brDate(data.data_ref)} · posição e MtM <b>líquidos</b> por contraparte (compra − venda){" "}
            {/* #60: as-of do DADO servido (snapshot diário / live) */}
            <AsOfBadge asOf={data.as_of ?? null} />
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* #30 IPCA ON: toggle Nominal·Indexado (espelha o CF por Book #64). */}
          <div
            className="inline-flex rounded-md border border-white/10 bg-indra-azul-deep p-0.5 text-xs"
            role="group"
            aria-label="Modo de marcação do MtM"
            data-testid="exposicao-toggle-indexado"
          >
            <button
              type="button"
              onClick={() => setIndexado(false)}
              aria-pressed={!indexado}
              className={`px-2.5 py-1 rounded transition-colors ${!indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
            >
              Nominal
            </button>
            <button
              type="button"
              onClick={() => setIndexado(true)}
              aria-pressed={indexado}
              className={`px-2.5 py-1 rounded transition-colors ${indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
            >
              Indexado (IPCA)
            </button>
          </div>
          <div className="bg-indra-azul-deep border border-white/10 rounded-md px-3 py-1.5 text-sm text-white font-mono">
            {brDate(data.data_ref)} 📅
          </div>
        </div>
      </div>

      {indexado && (
        <p className="text-[11px] text-emerald-300/70" data-testid="exposicao-nota-indexado">
          ✦ Modo Indexado: contratos reajustados pela inflação implícita (#218) · curva nominal
          — reajusta só o contrato (a perna fixa cresce com o IPCA), não a marcação/forward.
        </p>
      )}

      {/* Headlines: o líquido é o protagonista; direção é contexto de risco */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <MetricChip
          label={indexado ? "MtM líquido total (indexado)" : "MtM líquido total"}
          value={fmtBRL(hl.total, { abbreviate: true })}
          tone={hl.total >= 0 ? "positive" : "negative"}
          hint={fmtBRL(hl.total)}
        />
        <MetricChip
          label="Contrapartes net compradas"
          value={fmtBRL(hl.compradas, { abbreviate: true })}
          tone="positive"
          hint={fmtBRL(hl.compradas)}
        />
        <MetricChip
          label="Contrapartes net vendidas"
          value={fmtBRL(hl.vendidas, { abbreviate: true })}
          tone="negative"
          hint={fmtBRL(hl.vendidas)}
        />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Filtrar contraparte…"
          className="bg-indra-azul-deep border border-white/10 rounded-md px-3 py-1.5 text-sm text-white placeholder-white/30 w-64 focus:outline-none focus:border-white/30"
        />
        <p className="text-[11px] text-white/40">
          {busca.trim()
            ? `${linhasView.length} de ${data.linhas.length} contrapartes`
            : `${data.linhas.length} contrapartes`}{" "}
          · clique no cabeçalho pra ordenar · clique a linha pro detalhe
        </p>
      </div>

      <div className="bg-indra-azul-surface rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-4 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Contraparte</th>
                <th
                  onClick={() => clickSort("mwm")}
                  className={"text-right py-2 px-4 text-xs uppercase tracking-wide bg-indra-azul-deep cursor-pointer select-none hover:text-white " + (sortCol === "mwm" ? "text-white" : "text-white/60")}
                >
                  Posição líq. (MWm méd.){seta("mwm")}
                </th>
                <th
                  onClick={() => clickSort("mtm")}
                  className={"text-right py-2 px-4 text-xs uppercase tracking-wide bg-indra-azul-deep cursor-pointer select-none hover:text-white " + (sortCol === "mtm" ? "text-white" : "text-white/60")}
                >
                  MtM líquido{seta("mtm")}
                </th>
                <th className="text-right py-2 px-4 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Limite</th>
                <th className="text-right py-2 px-4 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Consumo</th>
              </tr>
            </thead>
            <tbody>
              {linhasView.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-white/40">
                    Nenhuma contraparte casa com “{busca}”.
                  </td>
                </tr>
              )}
              {linhasView.map((l) => {
                const key = `${l.nivel}:${l.contraparte}:${l.cnpj ?? ""}`;
                const open = aberta === key;
                return (
                  <Fragment key={key}>
                    <tr
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => setAberta(open ? null : key)}
                    >
                      <td className="py-3 px-4 text-sm text-white">
                        <span className="inline-block w-4 text-white/40 align-middle">
                          {open ? <ChevronDown size={14} className="inline" /> : <ChevronRight size={14} className="inline" />}
                        </span>
                        <span className="font-semibold">{l.contraparte}</span>
                        <DirBadge tipo={l.badge_tipo} label={l.badge_label} />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="inline-flex flex-col items-end gap-0.5">
                          <span className={"text-sm font-mono tabular-nums " + corValor(l.posicao_liquida_mwm)}>
                            {fmtMWm(l.posicao_liquida_mwm)}
                          </span>
                          <SparkPeriodo anos={porAno(l.detalhe.posicao.por_ano_submercado)} />
                        </span>
                      </td>
                      <td className={"py-3 px-4 text-right text-sm font-mono tabular-nums whitespace-nowrap " + corValor(mtmLinha(l, indexado))}>
                        {fmtBRL(mtmLinha(l, indexado), { decimals: 0 })}
                      </td>
                      <td className="py-3 px-4 text-right text-xs text-white/30 italic">— configurar</td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-[11px] px-2 py-0.5 rounded text-white/30 italic">s/ limite</span>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={5} className="p-0">
                          <Drill l={l} indexado={indexado} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-white/40 leading-relaxed max-w-3xl">
        Colunas <b>Limite / Consumo</b> entram com o engine de crédito (<b>#30</b> — {data.metadados.credito_gated}). Legenda
        consumo: ≤60% verde · 60-90% âmbar · &gt;90% (ou &lt;-100% no lado vendedor) vermelho.
        {!data.metadados.invariante_ok && (
          <span className="text-indra-negativo"> ⚠ invariante MtM não fechou — ver metadados.</span>
        )}
      </p>
    </div>
  );
}
